import { DEFAULT_LIST_VIEW_CONFIG } from '../utils/listViewDateRanges';

export const ROUTE_VIEW_MAP = {
  '/WorkScheduleTable': 'work_schedule',
  '/DeliveryChallanTable': 'delivery_challan',
  '/PumpClearanceTable': 'pump_clearance',
  '/DieselConsumptionTable': 'diesel_consumption',
  '/DesignMixTable': 'design_mix',
  '/SalesOrderTable': 'sales_order',
  '/GoodsReceiptNoteTable': 'goods_receipt_note',
  '/QuotationTable': 'quotation',
  '/PurchaseOrderTable': 'purchase_order',
  '/CheckInTable': 'check_in',
  '/SalesInvoiceTable': 'sales_invoice',
  '/CubeTestReportTable': 'cube_test_report',
  '/QcDeliveryChallanReport': 'qc_delivery_challan',
  '/DailyPlantLogTable': 'maintenance_daily',
  '/PreventiveMaintenanceTable': 'maintenance_service',
};

export const DATE_RANGE_OPTIONS = {
  today: { label: 'Today' },
  current_month: { label: 'Current Month' },
  last_month: { label: 'Last Month' },
  last_two_months: { label: 'Last Two Months' },
  last_three_months: { label: 'Last Three Months' },
  custom: { label: 'Custom Date Range' },
};

export const STANDARD_DATE_RANGE_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'current_month', label: 'Current Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'last_two_months', label: 'Last Two Months' },
  { value: 'last_three_months', label: 'Last Three Months' },
  { value: 'custom', label: 'Custom Date Range' },
];

export const STANDARD_DATE_RANGE_VALUES = STANDARD_DATE_RANGE_OPTIONS.map((option) => option.value);

export const RECORD_STATUS_OPTIONS = [
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

export const CUBE_TEST_RECORD_STATUS_OPTIONS = [
  { value: 'true', label: 'Active' },
  { value: 'closed', label: 'Closed' },
  { value: 'false', label: 'Inactive' },
];

const withStandardDateRanges = (schema) => ({
  ...schema,
  toolbarDateRanges: STANDARD_DATE_RANGE_VALUES,
  dateRangeOptions: STANDARD_DATE_RANGE_OPTIONS,
});

export const LIST_VIEW_SCHEMAS = {
  work_schedule: withStandardDateRanges({
    sectionTitle: 'Work Schedule View',
    statusOptions: [
      { value: 'not_started', label: 'Not Started' },
      { value: 'in_progress', label: 'In-Progress' },
      { value: 'to_complete', label: 'To Complete' },
      { value: 'completed', label: 'Completed' },
    ],
    recordStatusOptions: RECORD_STATUS_OPTIONS,
  }),
  delivery_challan: withStandardDateRanges({
    sectionTitle: 'Delivery Challan View',
    recordStatusOptions: RECORD_STATUS_OPTIONS,
  }),
  qc_delivery_challan: withStandardDateRanges({
    sectionTitle: 'QC Delivery Challan Report View',
    recordStatusOptions: RECORD_STATUS_OPTIONS,
  }),
  design_mix: withStandardDateRanges({
    sectionTitle: 'Design Mix View',
    recordStatusOptions: RECORD_STATUS_OPTIONS,
  }),
  sales_order: withStandardDateRanges({
    sectionTitle: 'Sales Order View',
    recordStatusOptions: RECORD_STATUS_OPTIONS,
  }),
  goods_receipt_note: withStandardDateRanges({
    sectionTitle: 'Goods Receipt Note View',
    recordStatusOptions: RECORD_STATUS_OPTIONS,
  }),
  quotation: withStandardDateRanges({
    sectionTitle: 'Quotation View',
  }),
  purchase_order: withStandardDateRanges({
    sectionTitle: 'Purchase Order View',
  }),
  check_in: withStandardDateRanges({
    sectionTitle: 'Vehicle Check In View',
  }),
  sales_invoice: withStandardDateRanges({
    sectionTitle: 'Sales Invoice View',
    showCustomerFilter: true,
  }),
  cube_test_report: withStandardDateRanges({
    sectionTitle: 'Cube Test Report View',
    recordStatusOptions: CUBE_TEST_RECORD_STATUS_OPTIONS,
  }),
  maintenance_daily: withStandardDateRanges({
    sectionTitle: 'Maintenance View',
    recordStatusOptions: RECORD_STATUS_OPTIONS,
  }),
  maintenance_service: withStandardDateRanges({
    sectionTitle: 'Service & Breakdown View',
    recordStatusOptions: RECORD_STATUS_OPTIONS,
  }),
  pump_clearance: withStandardDateRanges({
    sectionTitle: 'Pump Clearance View',
  }),
  diesel_consumption: withStandardDateRanges({
    sectionTitle: 'Diesel Consumption View',
    recordStatusOptions: RECORD_STATUS_OPTIONS,
  }),
};

export const LIST_VIEW_SETTINGS_UPDATED_EVENT = 'listViewSettingsUpdated';

export function getDefaultListViewConfig(viewKey) {
  const schema = LIST_VIEW_SCHEMAS[viewKey];
  const config = {
    ...DEFAULT_LIST_VIEW_CONFIG,
    plant_ids: [],
    customer_ids: [],
    default_date_range: viewKey === 'qc_delivery_challan' ? 'last_three_months' : 'current_month',
    enabled_date_ranges: schema?.toolbarDateRanges || STANDARD_DATE_RANGE_VALUES,
    custom_from_date: null,
    custom_upto_date: null,
  };
  if (viewKey === 'work_schedule') {
    config.schedule_statuses = ['completed', 'to_complete', 'in_progress', 'not_started'];
  }
  if (viewKey === 'cube_test_report') {
    config.record_statuses = ['true', 'closed', 'false'];
    config.record_status_options_version = 2;
  }
  return config;
}

/** Upgrade older Cube Test Active/Inactive prefs so Closed rows are included. */
export function normalizeCubeTestListViewConfig(config) {
  const next = { ...(config || getDefaultListViewConfig('cube_test_report')) };
  const version = Number(next.record_status_options_version || 1);
  if (version < 2) {
    const statuses = Array.isArray(next.record_statuses) ? [...next.record_statuses] : [];
    if (!statuses.includes('closed')) {
      const trueIdx = statuses.indexOf('true');
      if (trueIdx >= 0) {
        statuses.splice(trueIdx + 1, 0, 'closed');
      } else {
        statuses.push('closed');
      }
    }
    next.record_statuses = statuses.length ? statuses : ['true', 'closed', 'false'];
    next.record_status_options_version = 2;
  }
  return next;
}

export function dispatchListViewSettingsUpdated(viewKey) {
  window.dispatchEvent(new CustomEvent(LIST_VIEW_SETTINGS_UPDATED_EVENT, { detail: { viewKey } }));
}

export function getToolbarDateRanges(viewKey, config) {
  const schema = LIST_VIEW_SCHEMAS[viewKey];
  return config?.enabled_date_ranges?.length
    ? config.enabled_date_ranges
    : schema?.toolbarDateRanges || STANDARD_DATE_RANGE_VALUES;
}
